import { Component, inject, signal, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ImageUploadComponent } from './components/image-upload.component';
import { MapPickerComponent } from './components/map-picker.component';
import { MapService } from '../../../core/services/map.service';
import { ListingService } from '../../../core/services/listing.service';
import { ProfileService } from '../../../core/services/profile.service';
import { AuthService } from '../../../core/auth/auth.service';
import { LookupService } from '../../../core/services/lookup.service';
import { AlertService } from '../../../shared/services/alert.service';
import {LookupItem, Settlement} from '../../../core/models/lookup.model';

@Component({
  selector: 'app-new-announcement',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ImageUploadComponent, MapPickerComponent],
  templateUrl: './new-announcement.component.html',
  styleUrls: ['./new-announcement.component.scss']
})
export class NewAnnouncementComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private mapService = inject(MapService);
  private listingService = inject(ListingService);
  private profileService = inject(ProfileService);
  private lookupService = inject(LookupService);
  private alertService = inject(AlertService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  protected readonly Math = Math;

  announcementForm: FormGroup;
  isSubmitted = false;
  showSuccess = false;
  showAreaModal = false;
  isLoading = false;
  isMapLoading = signal<boolean>(false);
  isEditMode = false;
  editId: string | null = null;
  private isPatching = false;
  private initialFormValue: any = null;

  uploadedImageIds = signal<number[]>([]);
  uploadedImageUrls = signal<string[]>([]);
  uploadedImages = signal<{id: number, url: string}[]>([]);
  selectedAreaLabel = signal<string>('');
  isAuthenticated = signal<boolean>(false);

  propertyTypes: LookupItem[] = [];
  renovationStatuses: LookupItem[] = [];
  rentDurations: LookupItem[] = [];
  cities: LookupItem[] = [];
  districts: LookupItem[] = [];
  settlements: Settlement[] = [];

  officeObjectTypes = ['Biznes mərkəzi', 'Ev/Mənzil', 'Villa'];

  constructor() {
    this.announcementForm = this.fb.group({
      type: ['sale', Validators.required], // sale or rent
      propertyType: ['', Validators.required],
      officeObjectType: [''],
      cityId: [null, Validators.required],
      districtId: [null],
      settlementId: [null],
      address: ['', Validators.required],
      latitude: [null],
      longitude: [null],
      rooms: [''],
      area_m2: [''],
      land_area_sot: [''],
      floor: [''],
      total_floors: [''],
      repair: [''],
      description: ['', [Validators.required, Validators.maxLength(3000)]],
      price: ['', [Validators.required, Validators.min(1)]],
      hasExtract: [false],
      hasMortgage: [false],
      rentDuration: ['long-term'],
      ownerType: ['', Validators.required], // owner or agent
      contactName: ['', Validators.required],
      contactEmail: ['', [Validators.required, Validators.email]],
      contactPhone: ['', [
        Validators.required,
        Validators.pattern(/^(\+994 \((50|51|10|70|77|55|99)\) \d{3}-\d{2}-\d{2}|\+994 (50|51|10|70|77|55) \d{3} \d{2} \d{2})$/)
      ]],
      mediaIds: [[], [Validators.required, this.imageCountValidator(4, 20)]],
      metroId: [null]
    });

    this.setupDynamicValidators();
  }

  ngOnInit() {
    this.isAuthenticated.set(this.authService.isAuthenticated());
    this.loadLookups();
    this.checkEditMode();

    if (!this.isEditMode) {
      this.adjustValidatorsForAuthenticatedUser();
      if (this.isAuthenticated()) {
        this.loadUserProfile();
      }
    }
  }

  private adjustValidatorsForAuthenticatedUser() {
    const isGuest = !this.authService.isAuthenticated();

    if (!isGuest) {
      this.announcementForm.get('contactName')?.disable();
      this.announcementForm.get('contactPhone')?.disable();
      this.announcementForm.get('ownerType')?.disable();
    } else {
      this.announcementForm.get('contactName')?.enable();
      this.announcementForm.get('contactPhone')?.enable();
      this.announcementForm.get('contactPhone')?.setValidators([
        Validators.required,
        Validators.pattern(/^\+994(50|51|55|70|77|99|10)\d{7}$/)
      ]);
    }
    this.announcementForm.get('contactPhone')?.updateValueAndValidity();
  }

  private loadUserProfile() {
    this.profileService.getMe().subscribe({
      next: (res) => {
        if (res && res.data) {
          this.announcementForm.patchValue({
            contactName: res.data.name,
            contactEmail: res.data.email,
            contactPhone: res.data.phone,
            ownerType: res.data.packetId ? 'agent' : 'owner'
          });
        }
      }
    });
  }

  private checkEditMode() {
    this.editId = this.route.snapshot.paramMap.get('id');
    if (this.editId && this.router.url.includes('/edit/')) {
      this.isEditMode = true;
      this.adjustValidatorsForEditMode();
      this.loadListingData(this.editId);
    }
  }

  private adjustValidatorsForEditMode() {
    const controls = ['contactName', 'contactPhone', 'ownerType'];
    controls.forEach(controlName => {
      this.announcementForm.get(controlName)?.clearValidators();
      this.announcementForm.get(controlName)?.updateValueAndValidity();
    });
  }

  private loadListingData(id: string) {
    this.isLoading = true;
    this.profileService.getListingById(id).subscribe({
      next: (res) => {
        if (res && res.success && res.data) {
          this.patchFormWithData(res.data);
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.alertService.error('Məlumatların yüklənməsi zamanı xəta baş verdi');
        this.isLoading = false;
      }
    });
  }

  private patchFormWithData(data: any) {
    this.isPatching = true;
    this.announcementForm.patchValue({
      type: data.dealType === 1 ? 'sale' : 'rent',
      propertyType: data.propertyType?.id || data.propertyType,
      cityId: data.administrativeArea?.id || data.administrativeArea,
      address: data.address,
      latitude: data.latitude,
      longitude: data.longitude,
      rooms: data.roomCount?.id || data.roomCount,
      area_m2: data.areaSquareMeters,
      land_area_sot: data.landAreaSot,
      floor: data.floor,
      total_floors: data.totalFloors,
      repair: data.renovationStatus?.id || data.renovationStatus,
      description: data.description,
      price: data.price,
      hasExtract: data.hasDocument,
      hasMortgage: data.isMortgageAvailable,
      ownerType: data.isOwner ? 'owner' : 'agent',
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone
    });

    if (data.images && Array.isArray(data.images)) {
      this.uploadedImages.set(data.images);
      const ids = data.images.map((m: any) => m.id);
      const urls = data.images.map((m: any) => m.url);
      this.uploadedImageIds.set(ids);
      this.uploadedImageUrls.set(urls);
      this.announcementForm.get('mediaIds')?.setValue(ids);
    } else if (data.media && Array.isArray(data.media)) {
      this.uploadedImages.set(data.media);
      const ids = data.media.map((m: any) => m.id);
      const urls = data.media.map((m: any) => m.url);
      this.uploadedImageIds.set(ids);
      this.uploadedImageUrls.set(urls);
      this.announcementForm.get('mediaIds')?.setValue(ids);
    }

    // Əgər Bakı və ya Abşerondursa, rayonları və s. yükləmək lazımdır
    this.onCityChange();

    setTimeout(() => {
      this.announcementForm.patchValue({
        districtId: data.subAreaId,
        settlementId: data.settlementId
      });
      this.onDistrictChange();
    }, 500);

    setTimeout(() => {
      this.initialFormValue = this.announcementForm.getRawValue();
      this.isPatching = false;
    }, 1000);
  }

  private loadLookups() {
    this.lookupService.getPropertyTypes().subscribe(res => this.propertyTypes = res);
    this.lookupService.getRenovationStatuses().subscribe(res => this.renovationStatuses = res);
    this.lookupService.getRentDurations().subscribe(res => this.rentDurations = res);
    this.lookupService.getAdministrativeAreas().subscribe(res => this.cities = res);
  }

  onCityChange() {
    const cityId = this.announcementForm.get('cityId')?.value;
    if (!this.isPatching) {
      this.announcementForm.patchValue({
        districtId: null,
        settlementId: null,
        address: '',
        latitude: null,
        longitude: null
      });
    }
    this.districts = [];
    this.settlements = [];

    const city = this.cities.find(c => String(c.id) === String(cityId));
    if (city) {
      const cityName = city.name?.trim();
      if (cityName === 'Bakı') {
        this.lookupService.getBakuDistricts().subscribe(res => {
          this.districts = [...res];
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
        this.announcementForm.get('districtId')?.setValidators(Validators.required);
      } else if (cityName === 'Abşeron') {
        this.lookupService.getAbsheronAreas().subscribe(res => {
          this.districts = [...res];
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
        this.announcementForm.get('districtId')?.setValidators(Validators.required);
      } else {
        this.announcementForm.get('districtId')?.clearValidators();
      }
    } else {
      this.announcementForm.get('districtId')?.clearValidators();
    }
    this.announcementForm.get('districtId')?.updateValueAndValidity();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  onDistrictChange() {
    const cityId = this.announcementForm.get('cityId')?.value;
    const districtId = this.announcementForm.get('districtId')?.value;
    if (!this.isPatching) {
      this.announcementForm.patchValue({
        settlementId: null,
        address: '',
        latitude: null,
        longitude: null
      });
    }
    this.settlements = [];

    const city = this.cities.find(c => String(c.id) === String(cityId));
    if (city?.name?.trim() === 'Bakı' && districtId) {
      this.lookupService.getSettlements(districtId).subscribe(res => {
        this.settlements = [...res];
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    }
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  onSettlementChange() {
    if (!this.isPatching) {
      this.announcementForm.patchValue({
        address: '',
        latitude: null,
        longitude: null
      });
    }
  }

  private setupDynamicValidators() {
    this.announcementForm.get('propertyType')?.valueChanges.subscribe(val => {
      this.updateValidatorsByPropertyType(val);
    });

    this.announcementForm.get('type')?.valueChanges.subscribe(val => {
      this.updateValidatorsByPropertyType(this.announcementForm.get('propertyType')?.value);

      if (val === 'rent') {
        this.announcementForm.get('rentDuration')?.setValidators(Validators.required);
      } else {
        this.announcementForm.get('rentDuration')?.clearValidators();
      }
      this.announcementForm.get('rentDuration')?.updateValueAndValidity();
    });
  }

  private updateValidatorsByPropertyType(typeValue: any) {
    const typeId = Number(typeValue);
    const fields = ['rooms', 'area_m2', 'land_area_sot', 'floor', 'total_floors', 'repair', 'officeObjectType'];

    // Bütün spesifik sahələri təmizlə
    fields.forEach(f => {
      this.announcementForm.get(f)?.clearValidators();
    });

    // Biznes qaydalarına görə validasiya
    if (typeId === 1 || typeId === 2) { // Yeni tikili, Köhnə tikili
      this.setReq('rooms');
      this.setReq('area_m2');
      this.setReq('floor');
      this.setReq('total_floors');
      this.setReq('repair');
    } else if (typeId === 3) { // Həyət Evi / Villa
      this.setReq('rooms');
      this.setReq('area_m2');
      this.setReq('land_area_sot');
      this.setReq('total_floors'); // Evin mərtəbə sayı
      this.setReq('repair');
    } else if (typeId === 4 || typeId === 7) { // Ofis və Obyekt
      this.setReq('area_m2');
      // Floor və TotalFloors opsionaldır (istifadəçi daxil edə bilər, amma məcburi deyil)
      this.setReq('repair');
    } else if (typeId === 5) { // Qaraj
      this.setReq('area_m2');
    } else if (typeId === 6) { // Torpaq
      this.setReq('land_area_sot');
    }

    // İpoteka yalnız Satış üçündür
    const isSale = this.announcementForm.get('type')?.value === 'sale';
    const mortgageControl = this.announcementForm.get('hasMortgage');
    if (!isSale) {
      mortgageControl?.disable();
      mortgageControl?.setValue(false);
    } else {
      mortgageControl?.enable();
    }

    // Sahələrin statusunu yenilə
    fields.forEach(f => this.announcementForm.get(f)?.updateValueAndValidity());
  }

  private setReq(field: string) {
    const control = this.announcementForm.get(field);
    if (control) {
      const validators = [Validators.required];
      if (['area_m2', 'land_area_sot', 'rooms', 'price'].includes(field)) {
        validators.push(Validators.min(1));
      }
      control.setValidators(validators);
    }
  }

  onImagesChanged(ids: number[]) {
    this.uploadedImageIds.set(ids);
    this.announcementForm.get('mediaIds')?.setValue(ids);
    this.announcementForm.get('mediaIds')?.markAsTouched();
  }

  private imageCountValidator(min: number, max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || !Array.isArray(value)) return { required: true };
      if (value.length < min) return { minImages: { required: min, actual: value.length } };
      if (value.length > max) return { maxImages: { required: max, actual: value.length } };
      return null;
    };
  }

  onUrlsChanged(urls: string[]) {
    this.uploadedImageUrls.set(urls);
  }

  onPhoneInput(event: any): void {
    const input = event.target;
    let value = input.value;
    const selectionStart = input.selectionStart;
    const oldLength = value.length;

    // Yalnız rəqəmləri saxlayırıq
    let digits = value.replace(/\D/g, '');

    // Əgər +994 ilə başlayırsa, onu çıxarırıq (formatlamada yenidən əlavə olunacaq)
    if (digits.startsWith('994')) {
      digits = digits.substring(3);
    }

    // Maksimum 9 rəqəm (operator + nömrə)
    digits = digits.substring(0, 9);

    let formattedValue = '+994 ';
    if (digits.length > 0) {
      formattedValue += '(' + digits.substring(0, 2);
      if (digits.length > 2) {
        formattedValue += ') ' + digits.substring(2, 5);
        if (digits.length > 5) {
          formattedValue += '-' + digits.substring(5, 7);
          if (digits.length > 7) {
            formattedValue += '-' + digits.substring(7, 9);
          }
        }
      }
    }

    this.announcementForm.get('contactPhone')?.setValue(formattedValue, { emitEvent: false });

    // Kursorun mövqeyini hesablamaq və bərpa etmək
    setTimeout(() => {
      const newLength = formattedValue.length;
      let newSelection = selectionStart + (newLength - oldLength);

      // Kursorun yanlış yerdə qalmaması üçün tənzimləmə
      input.setSelectionRange(newSelection, newSelection);
    }, 0);
  }

  showMapPicker = signal<boolean>(false);

  showOnMap() {
    const cityId = this.announcementForm.get('cityId')?.value;
    const districtId = this.announcementForm.get('districtId')?.value;
    const settlementId = this.announcementForm.get('settlementId')?.value;

    const city = this.cities.find(c => String(c.id) === String(cityId));
    const district = this.districts.find(d => String(d.id) === String(districtId));
    const settlement = this.settlements.find(s => String(s.id) === String(settlementId));

    // Əgər artıq koordinatlar varsa və şəhər seçilməyibsə (və ya edit moddursa) birbaşa açırıq
    const currentLat = this.announcementForm.get('latitude')?.value;
    const currentLng = this.announcementForm.get('longitude')?.value;
    if (currentLat && currentLng && (!city || this.isEditMode)) {
      this.showMapPicker.set(true);
      return;
    }

    let searchQuery = 'Azərbaycan';
    if (city?.name) searchQuery += `, ${city.name}`;
    if (district?.name) searchQuery += `, ${district.name}`;
    if (settlement?.name) searchQuery += `, ${settlement.name}`;

    if (city) {
      this.isMapLoading.set(true);
      this.mapService.getCoordinates(searchQuery).subscribe({
        next: (coords) => {
          this.announcementForm.patchValue({
            latitude: coords.lat,
            longitude: coords.lng
          });
          this.isMapLoading.set(false);
          this.showMapPicker.set(true);
        },
        error: () => {
          this.isMapLoading.set(false);
          this.showMapPicker.set(true);
        }
      });
    } else {
      // Cari lokasiyanı götürməyə çalışırıq əgər şəhər seçilməyibsə
      if (navigator.geolocation) {
        this.isMapLoading.set(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.announcementForm.patchValue({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            // Cari koordinatlara görə ünvanı da yeniləyirik
            this.mapService.getAddressFromCoords(position.coords.latitude, position.coords.longitude)
              .subscribe({
                next: (address) => {
                  this.announcementForm.patchValue({ address });
                  this.isMapLoading.set(false);
                  this.showMapPicker.set(true);
                },
                error: () => {
                  this.isMapLoading.set(false);
                  this.showMapPicker.set(true);
                }
              });
          },
          () => {
            // Xəta və ya imtina halında sadəcə xəritəni açırıq
            this.isMapLoading.set(false);
            this.showMapPicker.set(true);
          },
          { timeout: 5000 } // 5 saniyə gözləyirik
        );
      } else {
        this.showMapPicker.set(true);
      }
    }
  }

  onLocationSelected(event: {lat: number, lng: number, address: string, metroId?: number | string}) {
    this.announcementForm.patchValue({
      latitude: event.lat,
      longitude: event.lng,
      address: event.address,
      metroId: event.metroId || null
    });
    this.announcementForm.get('address')?.markAsDirty();
  }

  onSubmit() {
    this.isSubmitted = true;

    if (this.announcementForm.invalid) {
      const mediaErrors = this.announcementForm.get('mediaIds')?.errors;
      if (mediaErrors) {
        if (mediaErrors['minImages']) {
          this.alertService.error(`Zəhmət olmasa minimum ${mediaErrors['minImages'].required} şəkil əlavə edin`);
        } else if (mediaErrors['maxImages']) {
          this.alertService.error(`Maksimum ${mediaErrors['maxImages'].required} şəkil əlavə edə bilərsiniz`);
        } else if (mediaErrors['required']) {
          this.alertService.error('Zəhmət olmasa şəkil əlavə edin');
        }
      } else {
        this.alertService.error('Zəhmət olmasa bütün vacib xanaları doldurun');
      }

      this.announcementForm.markAllAsTouched();
      const firstInvalidControl = document.querySelector('.ng-invalid');
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (this.isEditMode) {
      this.adjustValidatorsForEditMode();
    }

    this.isLoading = true;
    const formData = this.announcementForm.value;
    const requestBody = this.mapFormToRequest(formData);

    const isGuest = !this.authService.isAuthenticated();

    if (this.isEditMode) {
      this.profileService.updateListing(this.editId!, requestBody).subscribe({
        next: () => {
          this.isLoading = false;
          this.cdr.detectChanges();
          this.alertService.success('Elan uğurla yeniləndi');
          this.router.navigate(['/profile/my-ads'], { queryParams: { tab: 'pending' } });
        },
        error: (err) => {
          this.isLoading = false;
          this.cdr.detectChanges();
          const errorObj = err.error?.error || err.error;
          const errorCode = errorObj?.code || 'UNKNOWN_ERROR';
          const errorDetails = errorObj?.details;
          this.alertService.errorByCode(errorCode, errorDetails);
        }
      });
    } else {
      const createAction = isGuest
        ? this.listingService.createGuestListing(requestBody)
        : this.listingService.createListing(requestBody);

      createAction.subscribe({
        next: () => {
          this.isLoading = false;
          this.showSuccess = true;
          this.cdr.detectChanges();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        error: (err) => {
          this.isLoading = false;
          this.cdr.detectChanges();
          const errorObj = err.error?.error || err.error;
          const errorCode = errorObj?.code || 'UNKNOWN_ERROR';
          const errorDetails = errorObj?.details;
          this.alertService.errorByCode(errorCode, errorDetails);
          console.error('Create listing error:', err);
        }
      });
    }
  }

  private mapFormToRequest(formData: any): any {
    const typeId = Number(formData.propertyType);
    const isSale = formData.type === 'sale';
    const isGuest = !this.authService.isAuthenticated();

    const request: any = {
      dealType: isSale ? 1 : 2,
      propertyType: typeId,
      administrativeArea: formData.cityId ? Number(formData.cityId) : 0,
      subAreaType: formData.districtId ? 2 : 0,
      subAreaId: formData.districtId ? Number(formData.districtId) : 0,
      settlementId: formData.settlementId ? Number(formData.settlementId) : 0,
      address: formData.address || "",
      latitude: formData.latitude || 0,
      longitude: formData.longitude || 0,
      description: formData.description || "",
      price: Number(formData.price) || 0,
      isOwner: formData.ownerType === 'owner',
      mediaIds: formData.mediaIds || [],
      contactEmail: formData.contactEmail || "",

      // Əmlak növünə görə şərti sahələr
      roomCount: [1, 2, 3].includes(typeId) ? (formData.rooms ? Number(formData.rooms) : null) : null,
      areaSquareMeters: [1, 2, 3, 4, 5, 7].includes(typeId) ? (formData.area_m2 ? Number(formData.area_m2) : null) : null,
      landAreaSot: [3, 6].includes(typeId) ? (formData.land_area_sot ? Number(formData.land_area_sot) : null) : null,
      floor: [1, 2, 4, 7].includes(typeId) ? (formData.floor ? Number(formData.floor) : null) : null,
      totalFloors: [1, 2, 3, 4, 7].includes(typeId) ? (formData.total_floors ? Number(formData.total_floors) : null) : null,
      renovationStatus: [1, 2, 3, 4, 7].includes(typeId) ? (formData.repair ? Number(formData.repair) : null) : null,
      hasDocument: !!formData.hasExtract,
      isMortgageAvailable: isSale ? !!formData.hasMortgage : false
    };

    if (!this.isEditMode && isGuest) {
      request.contactName = formData.contactName || "";
      request.contactPhone = (formData.contactPhone || "").replace(/\s+/g, '');
    }

    if (formData.metroId) {
      request.metroId = Number(formData.metroId);
    }

    return request;
  }

  get isFormInvalid(): boolean {
    return this.announcementForm.invalid;
  }

  get isFormDirty(): boolean {
    if (!this.isEditMode) return true;
    if (!this.initialFormValue) return false;

    const currentValue = this.announcementForm.getRawValue();
    return JSON.stringify(this.initialFormValue) !== JSON.stringify(currentValue);
  }

  get propertyType(): string {
    return this.announcementForm.get('propertyType')?.value;
  }

  get announcementType(): string {
    return this.announcementForm.get('type')?.value;
  }
}
